// Clinic agents API (backend/api/agents/views.py). authedFetch sends the caller's token and
// throws Error(payload.error) on a non-2xx answer, with the HTTP status on err.status and the
// parsed body on err.payload. Nothing here wraps that error: callers read it as it comes.
import { authedFetch } from '../../../services/orderPaperwork';

// The sender id NotesAI Agent posts with in Messages (it has no users row). It must equal the
// backend's api.agents.config.AGENT_SENDER_ID: Messages recognizes an agent thread by this id,
// never by name. backend/api/tests/test_agents_trigger_text.py fails when the two differ.
export const AGENT_SENDER_ID = '00000000-0000-4000-8000-00000000a6e7';

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

const id = (value) => encodeURIComponent(value);

export const listAgents = () => authedFetch('agents/', { method: 'GET' });
// Which of a visit's form submissions the forms agent's assistant submitted for the patient,
// unsigned (views.agent_forms_assisted): { submissions: { <id>: { agent_id, ref, kind, answered,
// total, can_ask, runs_it?, assisted: [{ field, label, value, from }] } } }.
export const getAssistedForms = (schema, submissionIds) => authedFetch(
  `agents/forms/assisted/?schema=${id(schema)}&submission_ids=${id(submissionIds.join(','))}`,
  { method: 'GET' },
);
// The detail's runs and messages are its newest rows; pass `before` (an ISO time) and `beforeId`
// (the id of that row) taken from the oldest row shown to read the page before it. The server
// reads `before` without `before_id` as a plain time filter that leaves out rows at exactly that
// time, so send both. `before_id` without `before` is refused, so it is never sent alone.
export const getAgent = (agentId, { before = null, beforeId = null } = {}) => {
  const page = before ? `?before=${id(before)}${beforeId ? `&before_id=${id(beforeId)}` : ''}` : '';
  return authedFetch(`agents/${id(agentId)}/${page}`, { method: 'GET' });
};
export const planAgent = ({ instruction, patient = null }) =>
  authedFetch('agents/plan/', { body: { instruction, patient, timezone: browserTimezone() } });
export const createAgent = (agent) =>
  authedFetch('agents/', { body: { ...agent, timezone: agent.timezone || browserTimezone() } });
export const updateAgent = (agentId, patch) =>
  authedFetch(`agents/${id(agentId)}/`, { method: 'PATCH', body: patch });
export const deleteAgent = (agentId) => authedFetch(`agents/${id(agentId)}/`, { method: 'DELETE' });
export const pauseAgent = (agentId) => authedFetch(`agents/${id(agentId)}/pause/`);
export const resumeAgent = (agentId) => authedFetch(`agents/${id(agentId)}/resume/`);
export const runAgentNow = (agentId) => authedFetch(`agents/${id(agentId)}/run/`);
// A demo run: the task done once now, its changes and sends only described (views.agent_demo).
export const demoAgent = (agentId) => authedFetch(`agents/${id(agentId)}/demo/`);
// clientKey: a random id one message keeps across the person's retries, so the server can post it
// once (Plan 4 Task 9 dedupes on it; until then the server reads only `body` and ignores it).
export const sendAgentMessage = (agentId, body, clientKey = null) =>
  authedFetch(`agents/${id(agentId)}/messages/`, { body: clientKey ? { body, client_key: clientKey } : { body } });
export const markAgentSeen = (agentId) => authedFetch(`agents/${id(agentId)}/seen/`);
// A decision can wait up to 30 s behind another decision on the same run, and authedFetch has no
// timeout of its own, so the caller keeps its button busy until this settles.
export const decideCard = (cardId, { decision, note = null, answer = null }) =>
  authedFetch(`agents/cards/${id(cardId)}/decide/`, { body: { decision, note, answer } });
export const listMembers = (agentId) => authedFetch(`agents/${id(agentId)}/members/`, { method: 'GET' });
export const addMember = (agentId, userId) =>
  authedFetch(`agents/${id(agentId)}/members/`, { body: { user_id: userId } });
export const removeMember = (agentId, userId) =>
  authedFetch(`agents/${id(agentId)}/members/${id(userId)}/`, { method: 'DELETE' });
export const checkMembers = (agentId, userIds) =>
  authedFetch(`agents/${id(agentId)}/members/check/`, { body: { user_ids: userIds } });
export const leaveAgent = (agentId) => authedFetch(`agents/${id(agentId)}/leave/`);
export const setAgentEmail = (agentId, emailOk) =>
  authedFetch(`agents/${id(agentId)}/email/`, { body: { email_ok: emailOk } });
export const listNotes = (agentId) => authedFetch(`agents/${id(agentId)}/notes/`, { method: 'GET' });
export const deleteNote = (agentId, noteId) =>
  authedFetch(`agents/${id(agentId)}/notes/${id(noteId)}/`, { method: 'DELETE' });
// One record a reply links to (a source), by the reply's run and the handle the agent wrote. It
// answers, for a fax or a report, {kind, label, url, content_type}; for a visit, {kind, label,
// patient, date, provider, visit_type, open: {uuid, schema, date}}; for a patient, {kind, label,
// patient, open}; for a task, {kind, label, task_id}. Anything the caller can't open is a 404.
export const fetchAgentRef = (agentId, runId, handle) =>
  authedFetch(`agents/${id(agentId)}/ref/?run=${id(runId)}&h=${id(handle)}`, { method: 'GET' });
